import { MessagePost, SocketNotification } from '$/GraphQL/schema/schema';
import { logger } from '$/utils/logger';

const noop = (): void => {};

export type SocketNotificationHandlerFunction = (evt: SocketNotification) => void;
export type SocketHandlerFunction = (evt: Event) => void;

export interface ChatSocketOptions {
  protocols?: string | string[];
  timeoutSecs?: number;
  token?: string;
  refreshToken?: string;
  onopen?: (this: ChatSocket, ev: Event) => void;
  onreconnect?: (this: ChatSocket, ev: Event | CloseEvent) => void;
  onmaximum?: (this: ChatSocket, ev: CloseEvent) => void;
  onclose?: (this: ChatSocket, ev: CloseEvent) => void;
  onerror?: (this: ChatSocket, ev: Event) => void;
  onmessage?: (ev: SocketNotification) => void;
  skipConnect?: boolean;
}

export enum WebSocketEventType {
  open = 'open',
  close = 'close',
  message = 'message',
  error = 'error',
}

type EventHandler = (evt: Event) => void;

interface EventListeners {
  [event: string]: EventHandler[];
}

export default class ChatSocket {
  url: string;
  opts: ChatSocketOptions = {};
  num = 0;
  timer = 0;
  ws?: WebSocket;
  timeoutSecs: number;
  listeners: EventListeners = {};

  constructor(url: string, opts?: ChatSocketOptions) {
    this.opts = {
      ...(opts ?? {}),
    };

    this.timeoutSecs = this.opts.timeoutSecs ?? 5;
    this.url = url;

    this.onMessage = this.onMessage.bind(this);
    !this.opts.skipConnect && this.open();
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  public open(): void {
    logger.debug('CHAT', 'CS: open calling close');
    this.close(4001, 'UserOut'); // Close with UserOut so old socket won't reconnect

    logger.debug('CHAT', 'CS: new WS');
    this.ws = new WebSocket(`${this.url}`, this.opts.protocols || []);

    if (!this.ws) {
      console.error('WS: Unable to open Websocket');
      return;
    }

    this.terminated = false;
    this.ws.onopen = (e): void => {
      logger.debug('CHAT', 'CS: onopen', e);

      // @ts-ignore
      (this.opts.onopen || noop)(e);
      this.num = 0;

      // rebind listener once reconnected
      this.bindListeners();
    };

    this.ws.onclose = (e): void => {
      logger.debug('CHAT', 'CS: onclose', e);

      // const isNormalCose = e.code === 1000 || e.code === 1001 || e.code === 1005;

      // DD: We used to not retry on normal close, but I'm not sure why -- the server
      // can close the connection and we really want to reconnect I believe.  If we
      // need to not retry on specific situations we need to set that state prior to the close
      // via setState or a message prior to onclose that causes a setState

      const isUserLoggingOut = e.code === 4001 && e.reason === 'UserOut';
      const willRetry = !isUserLoggingOut;

      console.warn('WS onclose.  Retrying: ', willRetry, ' e.code: ', e.code);

      // Unbind listeners
      this.unbindListeners();
      // @ts-ignore
      (this.opts.onclose || noop)(e);

      if (willRetry) {
        this.reconnect(e);
      }
    };

    this.ws.onerror = (e): void => {
      const error = e as ErrorEvent;
      logger.error(`WS: ONERROR: ${error.message}`, { e });

      // @ts-ignore
      (this.opts.onerror || noop)(e);
      this.reconnect(e);
    };
  }

  protected sendRaw(data: string | ArrayBufferLike | Blob | ArrayBufferView): string | undefined {
    if (!this.ws) {
      return undefined;
    }

    try {
      this.ws.send(data);
    } catch (e) {
      const error = e as Error;
      logger.error('WS: ERROR sending message', e);
      return error.toString();
    }

    return undefined;
  }

  public send(post: MessagePost): string | undefined {
    if (!this.ws) {
      return undefined;
    }

    if (this.opts.token) {
      post.token = this.opts.token;
    }

    if (this.opts.refreshToken) {
      post.refreshToken = this.opts.refreshToken;
    }

    try {
      this.ws.send(JSON.stringify(post));
    } catch (e) {
      const error = e as Error;
      console.error('WS: ERROR sending message', { error });
      return error.toString();
    }

    return undefined;
  }

  public onMessage(e: any): void {
    let data: SocketNotification | undefined;

    try {
      data = JSON.parse(e.data) as SocketNotification;
    } catch (error) {
      console.error('WS: RECEIVE SOCKET ERROR: [', e.data, '] ', error);
      return;
    }

    if (!data) {
      console.error('WS: NO DATA', { e });
      return;
    }

    logger.debug('CHAT', 'WS: ON MESSAGE: ', { data });
    this.opts.onmessage && this.opts.onmessage(data);

    const handlers = this.listeners['message'];
    if (handlers) {
      handlers.forEach((handler) => (handler as any as (data: SocketNotification) => void)(data!));
    }
  }

  private bindListeners(): void {
    // Only one message event listener - us
    this.ws?.addEventListener('message', this.onMessage);

    Object.keys(this.listeners)
      .filter((event) => event !== 'message')
      .forEach((event) => {
        this.listeners[event].forEach((handler) => this.ws?.addEventListener(event, handler.bind));
      });
  }

  private unbindListeners(): void {
    Object.keys(this.listeners)
      .filter((event) => event !== 'message')
      .forEach((event) => {
        this.listeners[event].forEach((handler) => this.ws?.removeEventListener(event, handler));
      });

    // Only one message event listener - us
    this.ws?.removeEventListener('message', this.onMessage);
  }

  private addEventListener(event: string, handler: SocketHandlerFunction): void {
    if (!handler) {
      return;
    }
    this.listeners[event] = [...(this.listeners[event] || []), handler];

    if (event === 'message') {
      // handle this one ourselves
      return;
    }

    this.ws?.addEventListener(event, handler);
  }

  private removeEventListener(event: string, handler: SocketHandlerFunction): void {
    if (!this.listeners[event] || !handler) {
      return;
    }
    this.listeners[event] = this.listeners[event].filter((f) => f !== handler);

    if (event === 'message') {
      // handle this one ourselves
      return;
    }

    this.ws?.removeEventListener(event, handler);
  }

  public on(event: WebSocketEventType, handler: SocketHandlerFunction): void {
    this.addEventListener(event, handler);
  }

  public off(event: WebSocketEventType, handler: SocketHandlerFunction): void {
    this.removeEventListener(event, handler as SocketHandlerFunction);
  }

  public close(code?: number, reason?: string): void {
    logger.debug('CHAT', 'CS: close', { code, reason });
    clearTimeout(this.timer);
    this.timer = 0;
    if (!this.ws) {
      return;
    }

    this.ws.close(code || 1000, reason);
  }

  public reconnect(e: Event): void {
    if (this.terminated) {
      logger.debug('CHAT', 'CS: Socket terminated.');
      return;
    }

    if (this.timer) return;
    logger.debug('CHAT', 'CS: RECONNECT: ', { try: this.num });

    // @ts-ignore
    this.timer = setTimeout(
      () => {
        this.timer = 0;

        // @ts-ignore
        (this.opts.onreconnect || noop)(e);
        this.open();

        // Exponential back-off up to 60 seconds
      },
      Math.min(60 * 1000, Math.pow(2, this.num) * this.timeoutSecs * 1000)
    );

    this.num++;
  }

  public updateTokens(tokens: { token: string; refreshToken: string }): void {
    logger.debug('CHAT', 'CS: updateToken');
    this.opts.token = tokens.token;
    this.opts.refreshToken = tokens.refreshToken;
  }

  /**
   * Close and do not retry
   */
  private terminated = false;
  public terminate(): void {
    this.terminated = true;
    this.close(4001, 'UserOut');
  }
}
